import { useCallback, useState, useContext } from 'react'
import { AptiveIcon, Tab, Tabs, Modal } from '@aptive-env/storybook'
import { Ioffice, Iregion } from 'interface'
import { GoogleMap, Marker, Polygon } from '@react-google-maps/api'
import { ICoordinates } from 'interface/map'
import { routingColors } from 'constants/mockup'
import { AppContext } from 'App'

type OfficeModalProps = React.HTMLAttributes<HTMLDivElement> & {
  open: boolean
  selectedOffice: Ioffice,
  regions: Iregion[],
  onSelectOffice: (data: Ioffice) => void
  onClose: () => void
  center: ICoordinates | undefined
}

const containerStyle = {
  width: '100%',
  height: '455px',
}

function classNames(...classes: (string | boolean)[]) {
  return classes.filter(Boolean).join(' ')
}

export const OfficeModal: React.FC<OfficeModalProps> = (params) => {
  const { isGoogleMapAPILoaded, googleMapAPILoadError } = useContext(AppContext)
  const { open, className, selectedOffice, regions, center, onSelectOffice, onClose } = params
  const [checkedTab, setCheckedTab] = useState(0)

  const handleSelectOffice = useCallback((office: Ioffice) => {
    onSelectOffice(office)
  }, [onSelectOffice])

  const handleLoadError = (error: Error | undefined) => {
    console.error('Error loading Google Maps:', error)
  }

  if (googleMapAPILoadError) {
    handleLoadError(googleMapAPILoadError)
    return <div>Error loading Google Maps!</div>
  }

  const handleCloseModal = () => {
    onClose()
  }

  const handleChangeTab = (val: number) => {
    setCheckedTab(val)
  }

  const generatePolygonOption = (routeIndex: number) => {
    const polygonOption = {
      fillOpacity: 0.24,
      fillColor: routingColors[routeIndex % routingColors.length],
      strokeColor: routingColors[routeIndex % routingColors.length],
      strokeWeight: 2,
    }
    return polygonOption
  }

  const generateCircleSymbol = (routeIndex: number) => {
    const circleSymbol = {
      path: 'M-2.3 0a2.3 2.3 0 1 0 4.6 0a2.3 2.3 0 1 0 -4.6 0',
      strokeColor: '#000',
      fillColor: routingColors[routeIndex % routingColors.length],
      fillOpacity: 1,
      strokeWidth: 1,
      strokeOpacity: 1,
      scale: 2,
    }
    return circleSymbol
  }

  return (
    <Modal
      open={open}
      className={className}
      onClickOutside={handleCloseModal}
    >
      <div className="px-6 pt-6 pb-4 flex justify-between">
        <div className="flex items-center gap-2">
          <AptiveIcon className="w-[24px] h-[24px] stroke-ui-gray-600 fill-none" icon="globeAlt"></AptiveIcon>
          <div className="text-bodyBold text-ui-gray-600">Select an Office</div>
        </div>
        <div>
          <AptiveIcon className="w-[24px] h-[24px] stroke-ui-gray-600 fill-none cursor-pointer" icon="x" onClick={handleCloseModal}></AptiveIcon>
        </div>
      </div>
      <div className="relative px-4">
        <div className="absolute border-b-2 bottom-0 w-[calc(100%-32px)]"></div>
        <Tabs
          className="space-x-2 px-4"
          onChange={handleChangeTab}
          value={checkedTab}
        >
          <Tab className="border-b-[2px] w-[57px] pb-[13px] z-10" value={0}>
            List
          </Tab>
          <Tab className="border-b-[2px] w-[57px] pb-[13px] z-10" value={1}>
            Map
          </Tab>
        </Tabs>
      </div>
      <div className="p-6 flex gap-6 flex-wrap">
        {
          checkedTab === 0 &&
          regions?.map((region, index) => (
            <div className="w-1/6 flex flex-col gap-2" key={index}>
              <div className="text-smallBold text-[#101722] cursor-context-menu">{region.name}</div>
              {
                region.offices.map((office, cIndex) => (
                  <div key={'country_' + cIndex} className={classNames('text-small cursor-pointer', office.name === selectedOffice.name ? 'text-[#101722]' : 'text-[#5E6774]')} onClick={() => handleSelectOffice(office)}>{office.name}</div>
                ))
              }
            </div>
          ))
        }
        {
          checkedTab === 1 && isGoogleMapAPILoaded &&
          <GoogleMap
            zoom={4}
            center={center}
            mapContainerStyle={containerStyle}
          >
            {regions?.map((region: any, index: number) => (
              <div key={index}>
                {
                  region.offices.map((office: any, officeIndex: number) => (
                    <Marker
                      key={officeIndex}
                      icon={generateCircleSymbol(index)}
                      title={office.name}
                      position={office.location}
                      onClick={() => handleSelectOffice(office)}
                    />
                  ))
                }
                <Polygon
                  visible={true}
                  options={generatePolygonOption(index)}
                  paths={region.boundary}
                />
              </div>
            ))}
          </GoogleMap>
        }
      </div>
    </Modal>
  )
}
